import type { BalanceSheet, BalanceSheetRow } from '@erp/shared';
import Decimal from 'decimal.js';
import { AlertTriangle, Boxes, Coins, Landmark } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Card,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/index.js';
import { EmptyState, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney } from '../../lib/money.js';

import { KpiCard } from './_shared/KpiCard.js';

export function BalanceSheetPage(): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState<BalanceSheet | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setReport(null);
    void api()
      .get<BalanceSheet>(`/reports/balance-sheet?asOf=${asOf}`)
      .then((r) => setReport(r.data))
      .catch((e) => setError(String((e as Error).message ?? e)));
  }, [asOf]);

  const fmt = (amt: string): string =>
    report ? formatMoney(amt, { locale: i18n.language, currency: report.baseCurrencyCode }) : '—';

  const totalAssets = report ? new Decimal(report.totalAssets) : null;
  const totalLiab = report ? new Decimal(report.totalLiabilities) : null;
  const totalEquity = report ? new Decimal(report.totalEquity) : null;
  const sumLE = totalLiab && totalEquity ? totalLiab.plus(totalEquity) : null;
  const diff = totalAssets && sumLE ? totalAssets.minus(sumLE) : null;
  const balanced = diff !== null && diff.abs().lt(0.005);

  const hasActivity = report && (
    report.assets.length > 0 || report.liabilities.length > 0 || report.equity.length > 0
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('balanceSheet.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('balanceSheet.description')}</p>
        </div>
        <FormField id="bs-asof" label={t('balanceSheet.asOf')}>
          <Input id="bs-asof" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </FormField>
      </header>

      {error ? (
        <Card className="border-danger/40 bg-danger/5 p-4">
          <div className="flex items-start gap-2 text-sm text-danger" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="font-medium">{t('balanceSheet.loadFailed')}</p>
              <p className="mt-0.5 text-xs text-danger/80">{error}</p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <KpiCard
              tone="success"
              icon={Boxes}
              label={t('balanceSheet.totalAssets')}
              value={fmt(report?.totalAssets ?? '0')}
              loading={!report}
            />
            <KpiCard
              tone="warning"
              icon={Coins}
              label={t('balanceSheet.totalLiabilities')}
              value={fmt(report?.totalLiabilities ?? '0')}
              loading={!report}
            />
            <KpiCard
              tone={balanced ? 'brand' : 'danger'}
              icon={Landmark}
              label={t('balanceSheet.totalEquity')}
              value={fmt(report?.totalEquity ?? '0')}
              hint={
                diff && (balanced
                  ? t('balanceSheet.balanced')
                  : t('balanceSheet.imbalance', { amount: fmt(diff.toFixed(4)) }))
                || undefined
              }
              loading={!report}
            />
          </div>

          {!report ? (
            <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>
          ) : !hasActivity ? (
            <EmptyState
              icon={<Landmark className="h-8 w-8" aria-hidden />}
              title={t('balanceSheet.empty')}
              description={t('balanceSheet.emptyDescription')}
            />
          ) : (
            <>
              <Section title={t('balanceSheet.assets')} rows={report.assets} total={report.totalAssets} fmt={fmt} tone="success" t={t} />
              <Section title={t('balanceSheet.liabilities')} rows={report.liabilities} total={report.totalLiabilities} fmt={fmt} tone="warning" t={t} />
              <Section title={t('balanceSheet.equity')} rows={report.equity} total={report.totalEquity} fmt={fmt} tone="info" t={t} />

              <Card className="flex items-center justify-between p-4">
                <div>
                  <h2 className="text-sm font-semibold">{t('balanceSheet.totalLiabilitiesAndEquity')}</h2>
                  <p className="mt-1 text-xs text-fg-muted">
                    {balanced ? t('balanceSheet.balanced') : t('balanceSheet.imbalance', { amount: fmt((diff ?? new Decimal(0)).toFixed(4)) })}
                  </p>
                </div>
                <div className="text-end">
                  <div className="font-mono text-lg font-semibold tabular-nums">{fmt(sumLE?.toFixed(4) ?? '0')}</div>
                  <Badge variant={balanced ? 'success' : 'warning'} className="mt-1">
                    {t('balanceSheet.balances')}
                  </Badge>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  rows: BalanceSheetRow[];
  total: string;
  fmt: (amt: string) => string;
  tone: 'success' | 'warning' | 'info';
  t: (k: string) => string;
}

const TONE_SPINE: Record<SectionProps['tone'], string> = {
  success: 'before:bg-success',
  warning: 'before:bg-warning',
  info: 'before:bg-info',
};
const TONE_BAR: Record<SectionProps['tone'], string> = {
  success: 'bg-success/60',
  warning: 'bg-warning/60',
  info: 'bg-info/60',
};

function Section({ title, rows, total, fmt, tone, t }: SectionProps): JSX.Element {
  const totalN = new Decimal(total);
  const denom = totalN.abs().lte(0) ? new Decimal(1) : totalN.abs();
  return (
    <Card
      className={`relative overflow-hidden p-0 before:absolute before:inset-y-3 before:start-0 before:w-1 before:rounded-full ${TONE_SPINE[tone]}`}
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-border bg-bg-subtle/40 px-4 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
        <span className="font-mono text-sm font-semibold tabular-nums">{fmt(total)}</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">{t('balanceSheet.columns.code')}</TableHead>
            <TableHead>{t('balanceSheet.columns.name')}</TableHead>
            <TableHead className="w-1/3 text-end">{t('balanceSheet.columns.balance')}</TableHead>
            <TableHead className="w-40 text-end">{t('balanceSheet.columns.share')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const v = new Decimal(r.baseBalance);
            const sharePct = denom.gt(0)
              ? Math.min(100, v.abs().dividedBy(denom).times(100).toNumber())
              : 0;
            return (
              <TableRow key={r.accountId}>
                <TableCell className="font-mono text-xs">{r.accountCode}</TableCell>
                <TableCell className="font-medium">{r.accountName}</TableCell>
                <TableCell className="text-end font-mono tabular-nums">{fmt(r.baseBalance)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-bg-subtle">
                      <div
                        className={`h-full rounded-full ${TONE_BAR[tone]}`}
                        style={{ width: `${sharePct.toFixed(1)}%` }}
                        aria-hidden
                      />
                    </div>
                    <span className="w-10 text-end font-mono text-xs tabular-nums">
                      {sharePct.toFixed(0)}%
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
